import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Dot } from "../Dot";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { useLogoRepository } from "../../../data/repositories/LogoRepository";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faEllipsisVertical,
  faListUl,
} from "@fortawesome/free-solid-svg-icons";
import { useSuccessToast } from "../Toast/Toast";
import { useDialog } from "../Dialogs/dialog-base";
import { LogoStorageByOrgDialog } from "./LogoStorageByOrgDialog";
import { Menu, MenuItem } from "@mui/material";

interface LogoStorageProps {
  logo: BaseLogo;
  showRefreshButton?: boolean;
}

export function LogoStorage({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) {
  const isNumeric = logo.logoType === "NumberLogo";
  if (isNumeric)
    return (
      <NumberLogoStorage logo={logo} showRefreshButton={showRefreshButton} />
    );
  else
    return (
      <SinglePointLogoStorage
        logo={logo}
        showRefreshButton={showRefreshButton}
      />
    );
}

// Helper function to ensure all 11 numberset items are present
function getCompleteNumbersetStorage(
  numberLogosInStorage: { value: string; inStorage: number }[]
) {
  // Define all 11 items that should be present in a numberset
  const allNumbersetItems = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

  return allNumbersetItems.map((value) => {
    const existing = numberLogosInStorage.find((item) => item.value === value);
    return {
      value,
      inStorage: existing?.inStorage ?? 0,
    };
  });
}

export const NumberLogoStorage = ({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) => {
  const completeStorage = getCompleteNumbersetStorage(
    logo.numberLogosInStorage
  );

  return (
    <div className={"pr-1.5 flex flex-col gap-y-1 font-semibold"}>
      <div className="flex flex-row">
        <div className="block pt-1 text-xs mb-2 w-[80px]">
          <p className="inline uppercase pr-1">Number</p>
        </div>
        <div className="block pt-1 text-xs mb-2">
          <p className="inline uppercase pr-1">In Storage</p>
        </div>
      </div>

      {completeStorage.map((storage) => (
        <div key={storage.value} className="flex flex-row">
          <div className="flex flex-row w-[80px]">
            <div>{storage.value}</div>
          </div>
          <div className="flex flex-row items-center">
            <Dot
              className={`${
                Math.max(0, storage.inStorage ?? 0) > 0
                  ? "!bg-status-success"
                  : ""
              }`}
            />
            <div>{Math.max(0, storage.inStorage ?? 0)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SinglePointLogoStorage = ({
  logo,
  showRefreshButton = false,
}: LogoStorageProps) => {
  const displayStorage = Math.max(0, logo.inStorage ?? 0);
  const { profile } = useProfileContext();
  const { refreshStorageCount } = useLogoRepository();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const byOrgDialog = useDialog();

  const isAdmin = profile?.userOrganizationInformations.role === Role.Admin;
  const shouldShowAdminMenu = showRefreshButton && isAdmin;
  const isMenuOpen = Boolean(menuAnchor);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleRefresh = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleCloseMenu();
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshStorageCount(logo.id);
      useSuccessToast(
        "Storage count refresh has been queued. If the logo has an amount in storage, it should update soon."
      );
    } catch (error) {
      console.error("Failed to refresh storage count:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenByOrg = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleCloseMenu();
    byOrgDialog.openDialog();
  };

  return (
    <div className={"pr-1.5 flex space-x-1 items-center font-semibold"}>
      <Dot className={`${displayStorage > 0 ? "!bg-status-success" : ""}`} />
      {displayStorage}
      {shouldShowAdminMenu && (
        <span
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center"
        >
          <button
            onClick={handleOpenMenu}
            disabled={isRefreshing}
            className={`ml-1 p-1 rounded hover:bg-gray-100 transition-colors ${
              isRefreshing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
            title="Storage actions"
          >
            <FontAwesomeIcon
              icon={faEllipsisVertical}
              className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
          <Menu
            anchorEl={menuAnchor}
            open={isMenuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem onClick={handleRefresh} disabled={isRefreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="mr-2 w-4" />
              Refresh storage count
            </MenuItem>
            <MenuItem onClick={handleOpenByOrg}>
              <FontAwesomeIcon icon={faListUl} className="mr-2 w-4" />
              Show counts per vendor
            </MenuItem>
          </Menu>
          <LogoStorageByOrgDialog logo={logo} dialogOption={byOrgDialog} />
        </span>
      )}
    </div>
  );
};
